/**
 * Assessment Session Recovery
 *
 * Persists in-progress assessment state so that a browser refresh
 * during a 15-20 minute test doesn't lose all progress.
 *
 * Flow:
 * 1. On assessment start → saveSessionState()
 * 2. After each answer → saveSessionState() with updated answers
 * 3. On assessment complete/abandon → clearSessionState()
 * 4. On app mount / learner change → getRecoverableSession(learnerId) to check
 *    for an in-progress test BELONGING TO THAT LEARNER (AS-2026-07-P1-02)
 * 5. User chooses resume → restore state; or abandon → clearSessionState()
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 * dataLayer migration: D4 (8 april 2026).
 */

import { AssessmentAttempt, AssessmentBlueprint } from '../types';
import { ASSESSMENT_SESSION_MAX_AGE_MS } from '../../config/constants';
import { warn, error as logError, debug } from '../../utils/logger';
import { dataLayer } from '../../data/dataLayer';
import type { FamilyMasteryState } from '../../mastery';
export interface RecoverableSession {
  blueprint: AssessmentBlueprint;
  attempt: AssessmentAttempt;
  startTime: number;
  preFamilyMastery: Record<string, FamilyMasteryState>;
  savedAt: number;
}

/** Save the current assessment session state for crash recovery. */
export async function saveSessionState(blueprint: AssessmentBlueprint, attempt: AssessmentAttempt, startTime: number, preFamilyMastery: Record<string, FamilyMasteryState>): Promise<void> {
  try {
    const session: RecoverableSession = {
      blueprint,
      attempt,
      startTime,
      preFamilyMastery,
      savedAt: Date.now()
    };
    const success = await dataLayer.saveAssessmentSession(session);
    if (!success) {
      warn('Failed to save assessment session — storage may be full');
    } else {
      debug(`Assessment session saved: ${attempt.answers.length}/${blueprint.questions.length} answers`);
    }
  } catch (err) {
    logError('Failed to save assessment session state:', err);
  }
}

/**
 * AS-2026-07-P1-02 (July 2026) — SESSION OWNERSHIP.
 *
 * The recovery slot is device-wide, but an in-progress assessment belongs to
 * exactly one learner. Without this check, learner B could be offered — and
 * resume, and submit — learner A's half-finished toets.
 *
 * Truth table (`sessionLearnerId` = owner recorded on the attempt,
 * `currentLearnerId` = who is looking at the app right now):
 *
 * | session | current   | resume? | why                                    |
 * |---------|-----------|---------|----------------------------------------|
 * | absent  | absent    | yes     | legacy / single-account parity         |
 * | absent  | present   | no      | ownership cannot be proven             |
 * | present | absent    | no      | ownership cannot be proven             |
 * | present | equal     | yes     | owner returns                          |
 * | present | different | no      | different learner — never offer        |
 *
 * A denied session is deliberately left ON DISK: it is someone else's work in
 * progress, so clearing it here would destroy exactly the data this layer
 * exists to protect.
 */
export function isSessionOwnedBy(session: RecoverableSession, currentLearnerId?: string): boolean {
  const sessionLearnerId = session.attempt?.learnerId;
  if (!sessionLearnerId && !currentLearnerId) return true;
  if (!sessionLearnerId || !currentLearnerId) return false;
  return sessionLearnerId === currentLearnerId;
}

/**
 * Check for a recoverable in-progress assessment session belonging to
 * `currentLearnerId`. Pass the learner whose app is on screen; omit only in
 * genuinely single-account contexts.
 */
export async function getRecoverableSession(currentLearnerId?: string): Promise<RecoverableSession | null> {
  try {
    const session = await dataLayer.getAssessmentSession<RecoverableSession>();
    if (!session) return null;

    // AS-2026-07-P1-02: ownership FIRST — before any check that clears.
    if (!isSessionOwnedBy(session, currentLearnerId)) {
      debug('[Assessment] Recoverable session belongs to another learner — not offering, leaving it intact');
      return null;
    }
    if (Date.now() - session.savedAt > ASSESSMENT_SESSION_MAX_AGE_MS) {
      debug('Discarding stale assessment session (older than 2 hours)');
      await clearSessionState();
      return null;
    }
    if (!session.blueprint || !session.attempt || !session.attempt.answers || session.attempt.status !== 'in-progress') {
      debug('Discarding invalid assessment session');
      await clearSessionState();
      return null;
    }
    const answeredCount = session.attempt.answers.length;
    const totalQuestions = session.blueprint.questions.length;
    if (answeredCount >= totalQuestions) {
      debug('Assessment session is already complete — clearing');
      await clearSessionState();
      return null;
    }
    debug(`Recoverable assessment session found: ${answeredCount}/${totalQuestions} answers, saved ${Math.round((Date.now() - session.savedAt) / 60000)} minutes ago`);
    return session;
  } catch (err) {
    logError('Failed to read assessment session:', err);
    await clearSessionState();
    return null;
  }
}

/** Clear the saved assessment session state. */
export async function clearSessionState(): Promise<void> {
  try {
    await dataLayer.clearAssessmentSession();
    debug('Assessment session state cleared');
  } catch (err) {
    logError('Failed to clear assessment session state:', err);
  }
}